import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Award, Scale, HelpCircle, ChevronRight } from 'lucide-react';
import './TermsConditions.css';

export function TermsConditions() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="terms-page">
      {/* Ambient backgrounds */}
      <div className="terms-bg">
        <div className="terms-bg__orb terms-bg__orb--1" />
        <div className="terms-bg__orb terms-bg__orb--2" />
        <div className="terms-bg__grid" />
      </div>

      <div className="terms-container">
        {/* Breadcrumbs */}
        <div className="terms-breadcrumbs">
          <Link to="/">Home</Link>
          <ChevronRight size={12} />
          <span>Terms & Conditions</span>
        </div>

        {/* Header */}
        <header className="terms-header">
          <span className="terms-eyebrow">Operating Guidelines</span>
          <h1 className="terms-title">Terms & <span className="terms-title__accent">Conditions</span></h1>
          <p className="terms-subtitle">
            Effective Date: June 1, 2026. Welcome to Luminar Entertainments. These terms govern your ticketing transactions and event admissions.
          </p>
        </header>

        {/* Core pillars */}
        <div className="terms-highlights">
          <div className="terms-card">
            <div className="terms-card__icon"><Award size={24} /></div>
            <h3>Intellectual Property</h3>
            <p>All designs, code, assets, and spectacle content belong strictly to Luminar Entertainments.</p>
          </div>
          <div className="terms-card">
            <div className="terms-card__icon"><Scale size={24} /></div>
            <h3>Binding Purchases</h3>
            <p>Completing a transaction constitutes a binding contract subject to local jurisdiction limits.</p>
          </div>
          <div className="terms-card">
            <div className="terms-card__icon"><HelpCircle size={24} /></div>
            <h3>Admission Rules</h3>
            <p>Rights to admission are reserved under respective stadium and venue security codes.</p>
          </div>
        </div>

        {/* Layout */}
        <div className="terms-layout">
          {/* Sidebar */}
          <aside className="terms-sidebar">
            <div className="terms-sidebar__sticky">
              <h3>Table of Contents</h3>
              <nav>
                <a href="#acceptance">1. Acceptance of Terms</a>
                <a href="#ticketing">2. Ticketing & Checkout</a>
                <a href="#admission">3. Venue Rules & Conduct</a>
                <a href="#liability">4. Limitation of Liability</a>
                <a href="#ip">5. Proprietary Rights</a>
                <a href="#governing">6. Governing Law</a>
              </nav>
            </div>
          </aside>

          {/* Policy content */}
          <article className="terms-content">
            <section id="acceptance">
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing this website, purchasing digital tickets, or attending any event produced or managed by Luminar Entertainments, you agree to comply with and be bound by these Terms & Conditions. If you disagree with any portion of these legal provisions, please refrain from executing checkouts or accessing our systems.
              </p>
              <p>
                We reserve the right to modify these operational guidelines at any time without prior individual notifications. Your continued use of the website or attendance at events after updates constitute binding acceptance of the revised rules.
              </p>
            </section>

            <section id="ticketing">
              <h2>2. Ticketing & Checkout</h2>
              <p>
                All checkouts executed via this interface are subject to the following rules:
              </p>
              <ul>
                <li><strong>Secured Transactions:</strong> All checkouts utilize cryptographically secure hosted redirects via our authorized payment gateway partners.</li>
                <li><strong>Accuracy of Details:</strong> You are solely responsible for entering the correct email address and mobile phone number during checkout. Luminar Entertainments is not liable for tickets sent to incorrect email addresses due to user typography mistakes.</li>
                <li><strong>Verification Checksums:</strong> All transaction records are checked against real-time database logs. Any checksum mismatch or suspicious callback modification will immediately flag the booking as invalid.</li>
                <li><strong>Cancellations:</strong> Ticket bookings are final. For structured refunds, please refer specifically to our <Link to="/refund" className="terms-inline-link">Refund & Cancellation Policy</Link>.</li>
              </ul>
            </section>

            <section id="admission">
              <h2>3. Venue Rules & Conduct</h2>
              <p>
                Possession of a valid ticket generated by our database constitutes a license to enter the specific venue, subject to the following guidelines:
              </p>
              <p>
                The event coordinator, stadium administration, and security personnel reserve the absolute right to refuse entry or evict any individual behaving in a disorderly, unsafe, or abusive manner, without any obligation to refund.
              </p>
              <p>
                Furthermore, dangerous items, illicit drugs, commercial recording gears (except as authorized in press guidelines), and outside food/beverages are strictly prohibited in stadium areas.
              </p>
              <blockquote>
                <FileText size={16} />
                <strong>Admission Note:</strong> Security checks (including body pat-downs, electronic scanner sweeps, and baggage screening) are mandatory at all entry turnstiles.
              </blockquote>
            </section>

            <section id="liability">
              <h2>4. Limitation of Liability</h2>
              <p>
                Luminar Entertainments is dedicated to maintaining high-level safety. However, to the maximum extent permitted by applicable laws:
              </p>
              <ul>
                <li>We are not liable for any indirect, incidental, special, or consequential damages resulting from ticket bookings, technical server interruptions, or the cancellation of checkouts.</li>
                <li>Luminar Entertainments does not assume liability for physical injuries, personal losses, stolen baggage, or emotional distress sustained at event venues. Attendees enter stadium grounds voluntarily and at their own risk.</li>
                <li>Our total aggregate liability for any dispute or action related to ticket booking transactions is strictly limited to the actual amount paid for that specific ticket order.</li>
              </ul>
            </section>

            <section id="ip">
              <h2>5. Proprietary Rights</h2>
              <p>
                All digital resources, visual layouts, photographs, logos, brand wordmarks, text content, animations, and backend server endpoints rendered on this website belong exclusively to Luminar Entertainments.
              </p>
              <p>
                You may not scrape, copy, modify, distribute, or reverse-engineer any portion of this system or its digital ticketing PDF structures without explicit, signed administrative authorization.
              </p>
            </section>

            <section id="governing">
              <h2>6. Governing Law</h2>
              <p>
                These Terms & Conditions, operational regulations, and all transaction-related disputes are governed exclusively by the laws of India. Any litigation, mediation, or legal claims arising from our tickets, checkouts, or event production must be filed under the exclusive jurisdiction of the competent courts in <strong>Ernakulam, Kerala, India</strong>.
              </p>
            </section>
          </article>
        </div>
      </div>
    </div>
  );
}
